'use client';
import React, { useEffect, useRef } from 'react';


const tiers = [
  {
    price: '$49',
    name: 'Exterior Wash',
    desc: 'Hot foam wash · rinse · hand dry · tire shine · exterior only',
    tag: 'Quick Clean',
    tagColor: 'bg-blue-500/20 text-blue-300',
    highlight: false,
    image: '/assets/images/bentley_foam_wash.png',
    imageAlt: 'Bentley Continental GT covered in thick white foam during a premium exterior car wash',
  },
  {
    price: '$99',
    name: 'Basic Interior Detail',
    desc: 'Full interior vacuum · dashboard polish · steam clean',
    tag: 'Most Popular',
    tagColor: 'bg-white/10 text-white/70',
    highlight: false,
    image: '/assets/images/interior_detail.png',
    imageAlt: 'Professional detailer vacuuming and steam cleaning a luxury car interior with spotless leather seats',
  },
  {
    price: '$149',
    name: 'Full Basic Detail',
    desc: 'Complete basic interior detailing + exterior wash',
    tag: 'Best Value',
    tagColor: 'bg-blue-400/20 text-blue-300',
    highlight: false,
    image: '/assets/images/full_basic_detail.png',
    imageAlt: 'Detailer applying clay bar treatment to a gleaming luxury sports car exterior with ceramic sealant',
  },
  {
    price: '$199',
    name: 'Premium Package',
    desc: 'Full vacuum · leather conditioning · dashboard polish · steam clean · odour removal · window clean · door panels · carpet shampoo · seat deep clean',
    tag: 'Premium',
    tagColor: 'bg-yellow-400/20 text-yellow-300',
    highlight: true,
    image: '/assets/images/premium_interior_ultra_luxury.png',
    imageAlt: 'Ultra-premium Rolls Royce interior being deep cleaned with leather conditioning and carpet shampooing',
  },
];

const openSMS = (tierName?: string) => {
  const body = tierName
    ? `Hi! I'd like to book a ${tierName} appointment.`
    : 'Hi! I would like to book a car detail appointment.';
  const link = document.createElement('a');
  link.href = `sms:16474500681?body=${encodeURIComponent(body)}`;
  link.setAttribute('target', '_self');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export default function HeroSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cards = sectionRef.current?.querySelectorAll('.tier-card');
    cards?.forEach((card, i) => {
      setTimeout(() => {
        (card as HTMLElement).style.opacity = '1';
        (card as HTMLElement).style.transform = 'translateY(0)';
      }, 200 + i * 120);
    });
  }, []);

  return (
    <section
      ref={sectionRef}
      id="services"
      className="hero-gradient noise-overlay relative min-h-screen flex flex-col items-center justify-center pt-28 pb-16 px-6"
    >
      {/* Floating orbs for depth */}
      <div
        className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(59,130,246,0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
        }}
      />
      <div
        className="absolute bottom-1/3 right-1/4 w-80 h-80 rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(29,78,216,0.12) 0%, transparent 70%)',
          filter: 'blur(50px)',
        }}
      />

      {/* Eyebrow */}
      <div className="relative z-10 flex items-center gap-3 mb-6">
        <span className="flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-blue-500/30 px-4 py-2 rounded-full text-xs font-700 text-white tracking-wide">
          <span className="w-2 h-2 rounded-full bg-blue-400 availability-dot inline-block" />
          Available in your area · 2 slots open today
        </span>
      </div>

      {/* Headline */}
      <div className="relative z-10 text-center mb-4 max-w-3xl">
        <h1 className="font-fraunces text-5xl md:text-7xl lg:text-8xl text-white leading-[0.9] tracking-tight mb-4">
          Showroom Shine,<br />
          <span className="italic font-light" style={{ color: '#60A5FA' }}>at your door.</span>
        </h1>
        <p className="text-lg md:text-xl text-white/55 font-medium max-w-xl mx-auto leading-relaxed">
          We roll up, you stay in your pajamas. Premium interior detailing starting from $99. 
          Your car will feel brand new — inside and out.
        </p>
      </div>

      {/* Price-anchored tier cards */}
      <div className="relative z-10 w-full max-w-5xl mt-12 grid grid-cols-1 gap-5">
        {tiers.map((tier, i) => (
          <div
            key={tier.name}
            className={`tier-card bento-card glass-tile p-8 flex flex-col gap-4 cursor-pointer ${
              tier.highlight ? 'ceramic-glow' : ''
            }`}
            style={{
              opacity: 0,
              transform: 'translateY(30px)',
              transition: `opacity 0.6s ease, transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.35s ease`,
            }}
            onClick={() => openSMS(tier.name)}
          >
            {tier.highlight && (
              <div
                className="absolute inset-0 rounded-[28px] pointer-events-none"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, transparent 60%)',
                }}
              />
            )}

            <div className="flex items-start justify-between">
              <span
                className={`text-xs font-800 uppercase tracking-widest px-3 py-1 rounded-full ${tier.tagColor}`}
              >
                {tier.tag}
              </span>
              {tier.highlight && (
                <span className="text-xs font-600 text-blue-400">✦ Recommended</span>
              )}
            </div>

            <div className="price-display text-6xl md:text-7xl text-white">
              {tier.price}
            </div>

            <div className="w-full rounded-2xl overflow-hidden" style={{ height: '160px' }}>
              <img
                src={tier.image}
                alt={tier.imageAlt}
                className="w-full h-full object-cover"
              />
            </div>

            <div>
              <p className="text-xl font-700 text-white mb-1">{tier.name}</p>
              <p className="text-sm text-white/50 leading-relaxed font-medium">
                {tier.desc}
              </p>
              {tier.name === 'Basic Interior Detail' && (
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-xs font-700 uppercase tracking-widest text-white/40 mb-1">Optional Add-ons</p>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                    <span className="text-sm text-white/70 font-medium">🐾 Pet Hair Extraction</span>
                    <span className="text-sm font-700 text-blue-300">+$39</span>
                  </div>
                  <div className="flex items-center justify-between bg-white/5 border border-white/10 rounded-xl px-3 py-2">
                    <span className="text-sm text-white/70 font-medium">🧂 Salt Extraction</span>
                    <span className="text-sm font-700 text-blue-300">+$39</span>
                  </div>
                </div>
              )}
              {tier.name === 'Premium Package' && (
                <div className="mt-3 flex flex-col gap-2">
                  <p className="text-xs font-700 uppercase tracking-widest text-white/40 mb-1">Included Highlights</p>
                  <div className="flex items-center gap-2 bg-blue-500/10 border border-blue-400/20 rounded-xl px-3 py-2">
                    <span className="text-sm">🚿</span>
                    <span className="text-sm text-blue-300 font-600">Exterior Wash Included</span>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); openSMS(tier.name); }}
              className="mt-auto w-full py-3.5 rounded-2xl text-sm font-700 tracking-wide transition-all duration-200 btn-mint"
            >
              Book My Detail
            </button>
          </div>
        ))}
      </div>

      {/* Trust micro-copy */}
      <div className="relative z-10 mt-8 flex flex-wrap items-center justify-center gap-6 text-sm text-white/45 font-medium">
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          Fully insured
        </span>
        <span className="w-1 h-1 rounded-full bg-blue-500/40" />
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          Arrives in 2-hour window
        </span>
        <span className="w-1 h-1 rounded-full bg-blue-500/40" />
        <span className="flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-400">
            <rect width="20" height="14" x="2" y="5" rx="2"/><line x1="2" x2="22" y1="10" y2="10"/>
          </svg>
          Apple Pay · Card
        </span>
      </div>

      {/* Scroll indicator */}
      <div className="relative z-10 mt-12 flex flex-col items-center gap-2 opacity-40">
        <span className="text-xs font-medium text-white tracking-widest uppercase">Scroll to explore</span>
        <div className="w-5 h-8 rounded-full border-2 border-white/30 flex items-start justify-center pt-1.5">
          <div
            className="w-1 h-2 rounded-full bg-white/50"
            style={{ animation: 'scrollDot 2s ease infinite' }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes scrollDot {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(8px); opacity: 0.3; }
        }
      `}</style>
    </section>
  );
}